import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import type { RedirectRule } from '../../../../infrastructure/SiteConfigClient';

interface AddRedirectModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (sourceUrl: string, targetUrl: string) => Promise<void>;
  existingRedirects: RedirectRule[];
  isSubmitting?: boolean;
}

export const AddRedirectModal: React.FC<AddRedirectModalProps> = ({
  show,
  onHide,
  onSubmit,
  existingRedirects,
  isSubmitting = false,
}) => {
  const [sourceUrl, setSourceUrl] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) {
      setSourceUrl('');
      setTargetUrl('');
      setErrors({});
      setValidationError(null);
    }
  }, [show]);

  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url.startsWith('/') ? `https://example.com${url}` : url);
      return urlObj.pathname.length > 0 || url.startsWith('/');
    } catch {
      return false;
    }
  };

  const checkForLoop = (source: string, target: string): boolean => {
    const targetRule = existingRedirects.find((r) => r.sourceUrl === target);
    if (!targetRule) return false;

    if (targetRule.targetUrl === source) return true;

    return checkForLoop(source, targetRule.targetUrl);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!sourceUrl.trim()) {
      newErrors['sourceUrl'] = 'Source URL is required';
    } else if (!isValidUrl(sourceUrl)) {
      newErrors['sourceUrl'] = 'Invalid URL format';
    } else if (existingRedirects.some((r) => r.sourceUrl === sourceUrl)) {
      newErrors['sourceUrl'] = 'This source URL already exists';
    }

    if (!targetUrl.trim()) {
      newErrors['targetUrl'] = 'Target URL is required';
    } else if (!isValidUrl(targetUrl)) {
      newErrors['targetUrl'] = 'Invalid URL format';
    }

    if (sourceUrl.trim() && targetUrl.trim() && sourceUrl === targetUrl) {
      newErrors['targetUrl'] = 'Source and target URLs cannot be the same';
    }

    if (sourceUrl.trim() && targetUrl.trim() && checkForLoop(sourceUrl, targetUrl)) {
      setValidationError('This redirect would create a loop');
      return false;
    } else {
      setValidationError(null);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSourceUrlChange = (value: string) => {
    setSourceUrl(value);
    if (errors['sourceUrl']) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors['sourceUrl'];
        return newErrors;
      });
    }
    setValidationError(null);
  };

  const handleTargetUrlChange = (value: string) => {
    setTargetUrl(value);
    if (errors['targetUrl']) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors['targetUrl'];
        return newErrors;
      });
    }
    setValidationError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      try {
        await onSubmit(sourceUrl.trim(), targetUrl.trim());
        onHide();
      } catch (error) {
        setValidationError(error instanceof Error ? error.message : 'Failed to add redirect');
      }
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Redirect Rule</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {validationError && (
            <Alert variant="danger" className="mb-3">
              {validationError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Source URL *</Form.Label>
            <Form.Control
              type="text"
              value={sourceUrl}
              onChange={(e) => handleSourceUrlChange(e.target.value)}
              className={errors['sourceUrl'] ? 'is-invalid' : ''}
              placeholder="/old-page or https://example.com/old-page"
              disabled={isSubmitting}
            />
            <Form.Text className="text-muted">The URL that should be redirected</Form.Text>
            {errors['sourceUrl'] && <div className="invalid-feedback">{errors['sourceUrl']}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Target URL *</Form.Label>
            <Form.Control
              type="text"
              value={targetUrl}
              onChange={(e) => handleTargetUrlChange(e.target.value)}
              className={errors['targetUrl'] ? 'is-invalid' : ''}
              placeholder="/new-page or https://example.com/new-page"
              disabled={isSubmitting}
            />
            <Form.Text className="text-muted">The URL to redirect to</Form.Text>
            {errors['targetUrl'] && <div className="invalid-feedback">{errors['targetUrl']}</div>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Redirect'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

